import { useState, useCallback, useEffect } from 'react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  action?: ToastAction;
  durationMs?: number;
}

let nextId = 0;
const listeners = new Set<(toast: Toast) => void>();

/**
 * Show a toast. If `action` is provided, the toast renders a clickable action
 * button (e.g. Undo). The toast auto-dismisses after `durationMs` (default 4000;
 * pass a longer value such as 8000 for actions that need a generous undo window).
 */
export function showToast(
  type: ToastType,
  message: string,
  options?: { action?: ToastAction; durationMs?: number },
) {
  const toast: Toast = {
    id: String(++nextId),
    type,
    message,
    action: options?.action,
    durationMs: options?.durationMs,
  };
  listeners.forEach((fn) => fn(toast));
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((toast: Toast) => {
    setToasts((prev) => [...prev, toast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== toast.id));
    }, toast.durationMs ?? 4000);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useEffect(() => {
    listeners.add(addToast);
    return () => { listeners.delete(addToast); };
  }, [addToast]);

  return { toasts, removeToast };
}
